import {
  Button,
  Grid,
  GridItem,
  Input,
  FormLabel,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  Select,
  InputGroup,
  Box,
  InputLeftAddon,
  SimpleGrid,
  Text,
  InputRightAddon
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import React, { useEffect, useState } from 'react'
import ReactSelect from 'react-select'

import { formatNumber } from '../../../../helpers'
import { ICoursesInPerson } from '../../../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import { CoursesOnlineService } from '../../../../services/CoursesOnline/CoursesOnlineService'

interface IFormData {
  name: string
  thumbnail: string | File | null
  course_link: string
  full_price: string
  discount_percentage: string
  duration_in_hours: string | number
  duration_in_months: string | number
  promotional_bar: string
  cronometer_type?: string
  cronometer_end_date: string
  cronometer_time: string
  in_person: boolean
  live_online: boolean
  active_campaign_lists: string[]
  active_campaign_in_person_lists: string[]
  active_campaign_live_online_lists: string[]
  dynamic_ids: string[]
  objective_ids: string[]
  knowledge_area_ids: string[]
}

const CoursesOnlineModal = ({
  isOpen,
  onClose,
  editCourse,
  onSuccess
}: {
  isOpen: boolean
  onClose: () => void
  editCourse?: Partial<ICoursesInPerson>
  onSuccess?: () => void
}) => {
  const toast = useToast()
  const service = new CoursesOnlineService()

  const [knowledgeAreas, setKnowledgeAreas] = useState<{ id: number; name: string }[]>([])
  const [dynamics, setDynamics] = useState<{ id: number; name: string }[]>([])
  const [objectives, setObjectives] = useState<{ id: number; name: string }[]>([])
  const [activeCampaignLists, setActiveCampaignLists] = useState<{ id: string; name: string }[]>([])
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({})

  useEffect(() => {
    service.getKnowledgeAreas().then(setKnowledgeAreas)
    service.getDynamics().then(setDynamics)
    service.getActiveCampaignLists().then(setActiveCampaignLists)
    service.getObjectives().then(setObjectives)
  }, [])

  const [formData, setFormData] = useState<IFormData>({
    name: editCourse?.name || '',
    thumbnail: editCourse?.thumbnail || null,
    course_link: editCourse?.course_link || '',
    full_price: editCourse?.full_price || '0',
    discount_percentage: editCourse?.discount_percentage || '0',
    duration_in_hours: editCourse?.duration_in_hours || '0',
    duration_in_months: editCourse?.duration_in_months || '0',
    promotional_bar: editCourse?.promotional_bar || '0',
    cronometer_type: editCourse?.cronometer_type || '0',
    cronometer_end_date: editCourse?.cronometer_end_date || '0',
    cronometer_time: editCourse?.cronometer_time || '0',
    in_person: editCourse?.in_person || false,
    live_online: editCourse?.live_online || true,
    active_campaign_lists: editCourse?.active_campaign_lists || [],
    active_campaign_in_person_lists: editCourse?.active_campaign_in_person_lists || [],
    active_campaign_live_online_lists: editCourse?.active_campaign_live_online_lists || [],
    dynamic_ids: editCourse?.dynamic_ids || [],
    objective_ids: editCourse?.objective_ids || [],
    knowledge_area_ids: editCourse?.knowledge_area_ids || []
  })

  useEffect(() => {
    if (editCourse?.id) {
      loadCourse(editCourse.id)
    } else {
      setFormData({
        name: '',
        thumbnail: null,
        course_link: '',
        full_price: '0',
        discount_percentage: '0',
        duration_in_hours: '0',
        duration_in_months: '0',
        promotional_bar: '0',
        cronometer_type: '0',
        cronometer_end_date: '0',
        cronometer_time: '0',
        in_person: false,
        live_online: false,
        active_campaign_lists: [],
        active_campaign_in_person_lists: [],
        active_campaign_live_online_lists: [],
        dynamic_ids: [],
        objective_ids: [],
        knowledge_area_ids: []
      })
    }
  }, [editCourse])

  const loadCourse = async (id: number) => {
    const course = await service.getCourse(id)

    setFormData({
      name: course.name || '',
      thumbnail: course.thumbnail || null,
      course_link: course.course_link || '',
      full_price: formatNumber(course.full_price.replace('.', ',')) || '0',
      discount_percentage: formatNumber(course.discount_percentage.replace('.', ',')) || '0',
      duration_in_hours: course.duration_in_hours || '0',
      duration_in_months: course.duration_in_months || '0',
      promotional_bar: course.promotional_bar || '0',
      cronometer_type: course.cronometer_type || '0',
      cronometer_end_date: course.cronometer_end_date || '0',
      cronometer_time: course.cronometer_time || '0',
      in_person: false,
      live_online: false,
      active_campaign_lists: course.active_campaign_lists || [],
      active_campaign_in_person_lists: [],
      active_campaign_live_online_lists: [],
      dynamic_ids: course.dynamics.map((d) => d.id.toString()) || [],
      objective_ids: course.objectives.map((o) => o.id.toString()) || [],
      knowledge_area_ids: course.knowledge_areas.map((k) => k.id.toString()) || []
    })
  }

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      if (editCourse && editCourse.id !== undefined) {
        await service.update(editCourse.id, formData)
        toast({
          title: 'Curso editado com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
      } else {
        await service.create(formData)
        toast({
          title: 'Curso criado com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        onSuccess?.()
        setFormData({
          name: '',
          thumbnail: null,
          course_link: '',
          full_price: '0',
          discount_percentage: '0',
          duration_in_hours: '0',
          duration_in_months: '0',
          promotional_bar: '0',
          cronometer_type: '0',
          cronometer_end_date: '0',
          cronometer_time: '0',
          in_person: false,
          live_online: false,
          active_campaign_lists: [],
          active_campaign_in_person_lists: [],
          active_campaign_live_online_lists: [],
          dynamic_ids: [],
          objective_ids: [],
          knowledge_area_ids: []
        })
      }
      onClose()
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ errors: Record<string, string[]> }>
      toast({
        title: editCourse && editCourse.id !== undefined ? 'Erro ao editar curso' : 'Erro ao criar curso',
        description: 'Verifique os dados e tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true
      })
      if (axiosError.response?.data?.errors) {
        setErrors(axiosError.response.data.errors)
      }
    }
  }

  const clearForm = () => {
    setFormData({
      name: '',
      thumbnail: null,
      course_link: '',
      full_price: '0',
      discount_percentage: '0',
      duration_in_hours: '0',
      duration_in_months: '0',
      promotional_bar: '0',
      cronometer_type: '0',
      cronometer_end_date: '0',
      cronometer_time: '0',
      in_person: false,
      live_online: false,
      active_campaign_lists: [],
      active_campaign_in_person_lists: [],
      active_campaign_live_online_lists: [],
      dynamic_ids: [],
      objective_ids: [],
      knowledge_area_ids: []
    })
    setErrors({})
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{editCourse ? 'Editar ' : 'Adicionar novo '}curso</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSave}>
          <ModalBody overflowY="auto" maxHeight="60vh" pr={2}>
            <Grid templateColumns="repeat(2, 1fr)" gap={6}>
              {/* name */}
              <GridItem>
                <FormLabel htmlFor="name">Nome</FormLabel>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value
                    }))
                  }
                  type="text"
                />
                {errors.name ? <Text color="red">{errors.name}</Text> : null}
              </GridItem>

              {/* thumbnail */}
              <GridItem>
                <FormLabel htmlFor="thumbnail">Thumbnail</FormLabel>
                <Input
                  id="thumbnail"
                  name="thumbnail"
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      thumbnail: e.target.files && e.target.files[0] ? e.target.files[0] : null
                    }))
                  }
                />
                {errors.thumbnail ? <Text color="red">{errors.thumbnail}</Text> : null}
              </GridItem>

              {/* course link */}
              <GridItem>
                <FormLabel htmlFor="course_link">Link do curso</FormLabel>
                <Input
                  id="course_link"
                  name="course_link"
                  value={formData.course_link}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      course_link: e.target.value
                    }))
                  }
                />
                {errors.course_link ? <Text color="red">{errors.course_link}</Text> : null}
              </GridItem>

              {/* knowledge area */}
              <GridItem>
                <FormLabel htmlFor="knowledge_area_id">Área de conhecimento</FormLabel>
                <Box>
                  <ReactSelect
                    id="area"
                    value={
                      formData.knowledge_area_ids
                        ? formData.knowledge_area_ids.map((area) => {
                            const selectedArea = knowledgeAreas.find((a) => a.id === +area)
                            return { label: selectedArea?.name, value: area }
                          })
                        : []
                    }
                    isMulti
                    closeMenuOnSelect={false}
                    options={knowledgeAreas.map((area) => ({
                      label: area.name,
                      value: area.id
                    }))}
                    onChange={(selectedOptions: { value: string }[]) => {
                      const selectedValues = selectedOptions.map((option: { value: string }) => option.value)
                      setFormData({ ...formData, knowledge_area_ids: selectedValues })
                    }}
                    placeholder="Selecione uma ou mais áreas"
                  />
                </Box>
                {errors.knowledge_area_ids ? <Text color="red">{errors.knowledge_area_ids}</Text> : null}
              </GridItem>

              {/* campaign online */}
              <GridItem>
                <Box>
                  <FormLabel>Campanhas Online</FormLabel>
                  <ReactSelect
                    id="area"
                    value={
                      formData.active_campaign_lists
                        ? formData.active_campaign_lists.map((area) => {
                            const selectedCampaign = activeCampaignLists.find((a) => a.id === area)
                            return { label: selectedCampaign?.name, value: area }
                          })
                        : []
                    }
                    // disabled={saving}
                    isMulti
                    closeMenuOnSelect={false}
                    options={activeCampaignLists.map((campaign) => ({
                      label: campaign.name,
                      value: campaign.id
                    }))}
                    onChange={(selectedOptions: { value: string }[]) => {
                      const selectedValues = selectedOptions.map((option: { value: string }) => option.value)
                      setFormData({ ...formData, active_campaign_lists: selectedValues })
                    }}
                    placeholder="Selecione uma ou mais campanhas"
                  />
                </Box>
                {errors.active_campaign_lists ? <Text color="red">{errors.active_campaign_lists}</Text> : null}
              </GridItem>

              {/* dynamics */}
              <GridItem>
                <FormLabel htmlFor="dynamic_id">Dinâmica de Ensino</FormLabel>

                <ReactSelect
                  id="area"
                  value={
                    formData.dynamic_ids
                      ? formData.dynamic_ids.map((dynamic) => {
                          const selectedDynamic = dynamics.find((d) => d.id === +dynamic)
                          return { label: selectedDynamic?.name, value: dynamic }
                        })
                      : []
                  }
                  // disabled={saving}
                  isMulti
                  closeMenuOnSelect={false}
                  options={dynamics.map((dynamic) => ({
                    label: dynamic.name,
                    value: dynamic.id
                  }))}
                  onChange={(selectedOptions: { value: string }[]) => {
                    const selectedValues = selectedOptions.map((option: { value: string }) => option.value)
                    setFormData({ ...formData, dynamic_ids: selectedValues })
                  }}
                  placeholder="Selecione uma ou mais dinâmicas"
                />
                {errors.dynamic_ids ? <Text color="red">{errors.dynamic_ids}</Text> : null}
              </GridItem>

              {/* objectives */}
              <GridItem>
                <FormLabel htmlFor="dynamic_id">Objetivos do Curso</FormLabel>

                <ReactSelect
                  id="objetivos"
                  value={
                    formData.objective_ids
                      ? formData.objective_ids.map((objective) => {
                          const selectedObjective = objectives.find((d) => d.id === +objective)
                          return { label: selectedObjective?.name, value: objective }
                        })
                      : []
                  }
                  isMulti
                  closeMenuOnSelect={false}
                  options={objectives.map((objective) => ({
                    label: objective.name,
                    value: objective.id
                  }))}
                  onChange={(selectedOptions: { value: string }[]) => {
                    const selectedValues = selectedOptions.map((option: { value: string }) => option.value)
                    setFormData({ ...formData, objective_ids: selectedValues })
                  }}
                  placeholder="Selecione um ou mais objetivos"
                />
                {errors.objective_ids ? <Text color="red">{errors.objective_ids}</Text> : null}
              </GridItem>

              {/* prices */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <GridItem>
                  <FormLabel htmlFor="full_price">Preço cheio</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>R$</InputLeftAddon>
                    <Input
                      id="full_price"
                      name="full_price"
                      placeholder="0"
                      type="text"
                      value={formData.full_price}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          full_price: formatNumber(e.target.value)
                        }))
                      }
                    />
                  </InputGroup>
                  {errors.full_price ? <Text color="red">{errors.full_price}</Text> : null}
                </GridItem>
                {/* discount */}
                <GridItem>
                  <FormLabel htmlFor="discount_percentage">Desconto (%)</FormLabel>
                  <InputGroup>
                    <Input
                      id="discount_percentage"
                      name="discount_percentage"
                      placeholder="0"
                      type="text"
                      value={formData.discount_percentage}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          discount_percentage: formatNumber(e.target.value)
                        }))
                      }
                    />
                    <InputRightAddon>%</InputRightAddon>
                  </InputGroup>
                </GridItem>
                {/* discounted price */}
                <GridItem>
                  <FormLabel htmlFor="discounted_price">Preço final</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>R$</InputLeftAddon>
                    <Input
                      id="discounted_price"
                      name="discounted_price"
                      value={`${(
                        (+formData.full_price.replace(/[^\d,]/g, '').replace(',', '.') || 0) *
                        (1 - (+formData.discount_percentage.replace(/[^\d,]/g, '').replace(',', '.') || 0) / 100)
                      )
                        .toFixed(2)
                        .replace('.', ',')}`}
                      readOnly
                    />
                  </InputGroup>
                </GridItem>
              </SimpleGrid>

              {/* duration (hours) */}
              <GridItem>
                <FormLabel htmlFor="duration_in_hours">Duração (horas)</FormLabel>
                <Input
                  id="duration_in_hours"
                  name="duration_in_hours"
                  value={formData.duration_in_hours}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration_in_hours: e.target.value
                    }))
                  }
                  type="number"
                />
                {errors.duration_in_hours ? <Text color="red">{errors.duration_in_hours}</Text> : null}
              </GridItem>

              {/* duration (months) */}
              <GridItem>
                <FormLabel htmlFor="duration_in_months">Duração (meses)</FormLabel>
                <Input
                  id="duration_in_months"
                  name="duration_in_months"
                  value={formData.duration_in_months}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration_in_months: e.target.value
                    }))
                  }
                  type="number"
                />
                {errors.duration_in_months ? <Text color="red">{errors.duration_in_months}</Text> : null}
              </GridItem>

              {/* promotional bar */}
              <GridItem colSpan={2}>
                <FormLabel htmlFor="promotional_bar">Habilitar Barra de Promoção</FormLabel>
                <Box
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                  px={3}
                  py={2}
                  display="inline-block"
                  cursor="pointer"
                >
                  <Switch
                    id="promotional_bar"
                    name="promotional_bar"
                    isChecked={formData.promotional_bar === '1'}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        promotional_bar: e.target.checked ? '1' : '0',
                        cronometer_type: 'disabled',
                        cronometer_end_date: '',
                        cronometer_time: ''
                      }))
                    }
                    size="md"
                    colorScheme="teal"
                  />
                </Box>
                {errors.promotional_bar && <Text color="red">{errors.promotional_bar}</Text>}
              </GridItem>

              {formData.promotional_bar === '1' && (
                <>
                  <GridItem colSpan={2}>
                    <FormLabel htmlFor="cronometer_enabled">Cronômetro</FormLabel>
                    <Box
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                      px={3}
                      py={2}
                      display="inline-block"
                      cursor="pointer"
                    >
                      <Switch
                        id="cronometer_enabled"
                        name="cronometer_enabled"
                        isChecked={formData.cronometer_type !== 'disabled'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            cronometer_type: e.target.checked ? '' : 'disabled',
                            cronometer_end_date: '',
                            cronometer_time: ''
                          }))
                        }
                        size="md"
                        colorScheme="teal"
                      />
                    </Box>
                  </GridItem>

                  {formData.cronometer_type !== 'disabled' && (
                    <>
                      <GridItem colSpan={1}>
                        <FormLabel htmlFor="cronometer_type">Tipo de Cronômetro</FormLabel>
                        <Select
                          id="cronometer_type"
                          name="cronometer_type"
                          placeholder="Selecione o tipo"
                          value={formData.cronometer_type}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              cronometer_type: e.target.value,
                              cronometer_end_date: '',
                              cronometer_time: ''
                            }))
                          }
                        >
                          <option value="end_date">Data de término</option>
                          <option value="time">Renovação automática</option>
                        </Select>
                        {errors.cronometer_type && <Text color="red">{errors.cronometer_type}</Text>}
                      </GridItem>

                      {formData.cronometer_type === 'end_date' && (
                        <GridItem colSpan={1}>
                          <FormLabel htmlFor="cronometer_end_date">Data de término</FormLabel>
                          <Input
                            id="cronometer_end_date"
                            name="cronometer_end_date"
                            type="datetime-local"
                            value={
                              formData.cronometer_end_date
                                ? new Date(formData.cronometer_end_date).toISOString().slice(0, 16)
                                : ''
                            }
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                cronometer_end_date: e.target.value
                              }))
                            }
                          />
                          {errors.cronometer_end_date && <Text color="red">{errors.cronometer_end_date}</Text>}
                        </GridItem>
                      )}

                      {formData.cronometer_type === 'time' && (
                        <GridItem colSpan={1}>
                          <FormLabel htmlFor="cronometer_time">Renovação automática (dias)</FormLabel>
                          <Input
                            id="cronometer_time"
                            name="cronometer_time"
                            type="number"
                            value={formData.cronometer_time || ''}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                cronometer_time: e.target.value
                              }))
                            }
                          />
                          {errors.cronometer_time && <Text color="red">{errors.cronometer_time}</Text>}
                        </GridItem>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                clearForm()
                onClose()
              }}
            >
              Fechar
            </Button>
            <Button colorScheme="magenta" type="submit">
              Salvar
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default CoursesOnlineModal
